import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BrowserHelper } from 'src/app/core/helper/browser.helper';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Utilities } from 'src/app/core/helper/util';
import { Log } from 'src/app/core/log';
import { Community } from 'src/app/data/schema/community';
import { CommunityUser } from 'src/app/data/schema/community-user';
import { SupportRequest, SupportStatus } from 'src/app/data/schema/support/support-request';
import { AiService } from 'src/app/data/service/ai.service';

@Component({
  selector: 'app-support-ticket',
  templateUrl: './support-ticket.page.html',
  styleUrls: ['./support-ticket.page.scss'],
})
export class SupportTicketPage {

  @ViewChild('fileInput') fileInput: ElementRef;
  
  // is page being access with guid, i.e. without community context
  isExternal: boolean = true;
  
  froalaOptions: any;

  get SupportStatus() { return SupportStatus; }

  isL57Admin: boolean = false;

  tickets: SupportRequest[] = [];
  newTicket: SupportRequest;
  loaded: boolean = false;
  acceptAssignment: boolean = false; // to ignore initialization change events from select
  currAssignedId: number;

  selectedFiles: FileList;

  fromEmail: string;// set from profile if possible, preserve after save of ticket

  constructor(protected activatedRoute: ActivatedRoute, private aiSvc: AiService) {
    ServiceHelper.menuCtrl.enable(false);
    activatedRoute.params.subscribe(params => {
      if ( params['guid'] != null ) {
        this.loadFromGuid(params['guid']);
        this.froalaOptions = Utilities.standardFroalaOptions(null);
      } else {
        this.isExternal = false;
        ServiceHelper.menuCtrl.enable(true);
        this.loadFromId(+params['id']);
        this.froalaOptions = Utilities.standardFroalaOptions(Community.activeCommunity?.communityId);
      }
    });
  }

  back(){
    ServiceHelper.navCtrl.navigateRoot('support/tickets');
  }

  onFileSelected(event) {
    this.selectedFiles = event.target.files;
  }

  download(path: string) {
      BrowserHelper.staticOpenUrl(path);
  }

  async save() {
    const load = await ServiceHelper.displayLoad('Saving...');
    try {
      // assign parent if this is a reply
      if ( this.tickets.length > 0 ) {
        this.newTicket.parentId = this.tickets[0].supportRequestId;
      }
      // target email should be replace with source, as it represents how the
      // ticket got into the system, ie from which email, or which page
      this.newTicket.source = 'CBS Dash';

      // if community is selected, set it on the ticket
      this.newTicket.communityId = Community.activeCommunity?.communityId;

      let savedObj = await this.aiSvc.submitTicket(this.selectedFiles, this.newTicket);
      savedObj = new SupportRequest(savedObj);
      this.tickets.push(savedObj);

      // and clear
      this.newTicket = new SupportRequest();
      
      this.newTicket.userEmail = this.fromEmail;
      this.selectedFiles = undefined;
      this.fileInput.nativeElement.value = '';
      await load.dismiss()
    }catch(error) {
      await load.dismiss();
      ServiceHelper.displayAlert('Error saving ticket', 'An error occurred:<br>' + error);
    }
  }

  async delete() {
    if ( await this.tickets[0].delete(null, 'support')) {
      this.back();
    }
  }

  async assign() {
    if ( this.currAssignedId != this.tickets[0].assignedUserId && this.tickets[0].assignedUserId > 0 ) {
      this.saveStatus(SupportStatus.Assigned);
    }
  }

  async saveStatus(status: SupportStatus) {
    if (this.acceptAssignment !== true ){return;}

    Log.debug('accepting assignment...')

    let action = 'Close';
    if ( status == SupportStatus.Assigned ) { action = 'Assign'; }
    else  if ( status == SupportStatus.New ) { action = 'Reopen';  }

    if ( await ServiceHelper.confirmSomething(`${action} Ticket?`, `Are you sure you want to ${action} this ticket?`)) {
      const load = await ServiceHelper.displayLoad('Saving...');
      try {
        let origTicket = this.tickets[0];
        origTicket.status = status;
        await origTicket.save()
        await load.dismiss()
      }catch(error) {
        await load.dismiss();
        ServiceHelper.displayAlert('Error saving ticket', 'An error occurred:<br>' + error);
      }
    }
  }

  private async loadFromGuid(guid: string) {
    const ticket = new SupportRequest(await SupportRequest.loadByGuid(guid));
    this.tickets.push(ticket);
    for(let reply of ticket.replies) {
      this.tickets.push(new SupportRequest(reply) );
    }
    this.init();
  }

  private async loadFromId(id: number) {
    if ( id > 0 ) {
      const ticket = new SupportRequest(await SupportRequest.load(id));
      this.tickets.push(ticket);
      for(let reply of ticket.replies) {
        this.tickets.push(new SupportRequest(reply) );
      }
    } 
    this.init();
  }

  private async init() {
    // if we have a ticket, make note of assigned user
    if ( this.tickets.length > 0 ) {
      this.currAssignedId = this.tickets[0].assignedUserId;
    } 
    
    this.newTicket = new SupportRequest();
    if ( Community.activeCommunity != null ) {
      const profile = await CommunityUser.myProfile();
      this.newTicket.userEmail = profile.contactEmail; 
    } else if ( this.tickets.length > 0 ) {
      this.newTicket.userEmail = this.tickets[0].userEmail; 
    }
    this.fromEmail = this.newTicket.userEmail;
    this.loaded = true;

    this.isL57Admin = ServiceHelper.authSvc.profile?.username?.endsWith('launch57.com');

    await Utilities.wait(300);
    Log.debug('now accept assignment!')
    this.acceptAssignment = true;
  }

}

